import { useCallback, useEffect, useRef, useState } from "react";
import { organizationService } from "../services";
import { useDebouncedValue } from "./useDebouncedValue";

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

function mergeOrgLists(existing, incoming) {
  const byId = new Map(existing.map((o) => [o.id, o]));
  for (const org of incoming) {
    byId.set(org.id, org);
  }
  return Array.from(byId.values());
}

/**
 * Server-paginated organization list with infinite scroll in Autocomplete listbox.
 */
export function useOrganizationInfiniteList({
  pageSize = DEFAULT_PAGE_SIZE,
} = {}) {
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);

  const [organizations, setOrganizations] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const loadingRef = useRef(false);

  const fetchPage = useCallback(
    async (pageToLoad, search, append) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      const fetchId = ++fetchIdRef.current;
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);
        const trimmed = search.trim();
        const { items, pagination } =
          await organizationService.getOrganizationsPage({
            page: pageToLoad,
            limit: pageSize,
            search: trimmed || undefined,
          });
        if (fetchId !== fetchIdRef.current) return;
        setOrganizations((prev) =>
          append ? mergeOrgLists(prev, items) : items,
        );
        setPage(pagination.page ?? pageToLoad);
        setHasMore(Boolean(pagination.hasNextPage));
      } catch (e) {
        if (fetchId !== fetchIdRef.current) return;
        console.error("Failed to load organizations", e);
        setError(
          e.response?.data?.message ||
            e.message ||
            "Failed to load organizations",
        );
        if (!append) {
          setOrganizations([]);
        }
        setHasMore(false);
      } finally {
        if (fetchId === fetchIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [pageSize],
  );

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setOrganizations([]);
    fetchPage(1, debouncedSearch, false);
  }, [debouncedSearch, fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchPage(page + 1, debouncedSearch, true);
  }, [hasMore, page, debouncedSearch, fetchPage]);

  const handleListboxScroll = useCallback(
    (event) => {
      const node = event.currentTarget;
      const nearBottom =
        node.scrollTop + node.clientHeight >= node.scrollHeight - 48;
      if (nearBottom) {
        loadMore();
      }
    },
    [loadMore],
  );

  const resetSearch = useCallback(() => {
    setSearchText("");
  }, []);

  return {
    organizations,
    loading,
    loadingMore,
    hasMore,
    error,
    searchText,
    setSearchText,
    debouncedSearch,
    handleListboxScroll,
    resetSearch,
  };
}
